import React from "react";
import { Link } from "react-router-dom";
import { Container, SectionLabel } from "../components/ui-kit";
import PageHero from "../components/PageHero";
import { Briefcase, Users, HandHeart, BarChart3, ArrowRight, Building2 } from "lucide-react";

const FEATURES = [
  ["CSR campaign design", "Design structured campaigns aligned to your CSR strategy."],
  ["Employee engagement", "Involve teams in meaningful, trackable participation."],
  ["Corporate volunteering", "Coordinate volunteering opportunities with partners."],
  ["Impact tracking", "Measure and report outcomes with clarity."],
  ["Program coordination", "End-to-end coordination with verified partners."],
  ["Digital reporting", "Downloadable CSR documentation and reports."],
];

const PORTAL = ["Create CSR account", "Select programs", "Sponsor initiatives", "Track impact", "Download reports", "View contribution history", "Manage employees", "Participate in volunteering"];

export default function CSR() {
  return (
    <div>
      <PageHero
        label="CSR & Partnerships"
        title="Make CSR part of your business impact"
        subtitle="Helping Hands enables businesses to turn CSR commitments into structured, measurable and transparent social-impact initiatives."
        crumbs={[{ label: "CSR & Partnerships" }]}
      />

      <section id="corporate" className="scroll-mt-28 bg-white">
        <Container className="py-16">
          <SectionLabel>Corporate CSR</SectionLabel>
          <h2 className="mt-4 max-w-2xl font-display text-4xl font-bold text-emerald-900">A partnership approach to social impact</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map(([title, text]) => (
              <div key={title} className="rounded-2xl border border-emerald-900/10 bg-sand p-6">
                <h3 className="font-display text-xl font-bold text-emerald-900">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section id="employee" className="scroll-mt-28 bg-emerald-950 text-white">
        <Container className="grid items-center gap-12 py-16 lg:grid-cols-2">
          <div id="volunteering" className="scroll-mt-28">
            <SectionLabel light>Corporate CSR Portal</SectionLabel>
            <h2 className="mt-4 font-display text-4xl font-bold">A future-ready portal for corporate partners</h2>
            <p className="mt-4 text-lg text-emerald-50/80">The architecture is ready today. Businesses will be able to manage their entire CSR journey in one place.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {PORTAL.map((p) => (
              <div key={p} className="flex items-center gap-2 rounded-xl border border-white/10 bg-emerald-900/40 px-4 py-3 text-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-gold-light" /> {p}
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section id="partner" className="scroll-mt-28 bg-sand">
        <Container className="py-16">
          <div className="grid gap-8 md:grid-cols-3">
            {[[Briefcase, "Become a CSR Partner"], [Users, "Strategic Partnerships"], [HandHeart, "Program Sponsorship"]].map(([Icon, title]) => (
              <div key={title} className="rounded-2xl border border-emerald-900/10 bg-white p-7 text-center">
                <Icon className="mx-auto h-8 w-8 text-emerald-800" />
                <h3 className="mt-4 font-display text-xl font-bold text-emerald-900">{title}</h3>
              </div>
            ))}
          </div>
          <div className="mt-12 rounded-3xl bg-emerald-900 p-10 text-center text-white">
            <Building2 className="mx-auto h-8 w-8 text-gold-light" />
            <h2 className="mt-4 font-display text-3xl font-bold">Turn your CSR vision into measurable impact</h2>
            <Link to="/contact" data-testid="csr-build-program" className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-bold text-white transition hover:bg-gold-light">Build a CSR Program <ArrowRight className="h-4 w-4 rtl:rotate-180" /></Link>
          </div>
        </Container>
      </section>
    </div>
  );
}
